import React from 'react';
import { Container, GlobalStyles } from '@mui/material';
import Grid2 from '@mui/material/Grid2';

import { useAuth } from '../auth/AuthContext';
import { hasAccountingAccess } from '../../subscription';
import EvaluationAccessView from './evaluation/EvaluationAccessView';
import EvaluationHero from './evaluation/EvaluationHero';
import EvaluationResultCard from './evaluation/EvaluationResultCard';
import EvaluationSidebarCard from './evaluation/EvaluationSidebarCard';
import useEvaluationData from './evaluation/useEvaluationData';

export default function Evaluation() {
    const { user } = useAuth();
    const hasAccounting = hasAccountingAccess(user?.paymentPlan);

    const {
        year,
        month,
        quarter,
        loading,
        exporting,
        message,
        error,
        data,
        mode,
        selectedMode,
        vatDetails,
        vatDetailsLoading,
        vatDetailsError,
        vatDetailsOpen,
        reportLabel,
        setYear,
        setMonth,
        setQuarter,
        setSelectedMode,
        handleRunSelected,
        handleRefresh,
        handleDownloadPdf,
        handleVatDetailsToggle,
    } = useEvaluationData();

    const handlePrint = React.useCallback(() => {
        window.print();
    }, []);

    const handleDownload = React.useCallback(async () => {
        const response = await handleDownloadPdf();
        if (!response?.data) {
            return;
        }
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename || 'steuer-auswertung.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, [handleDownloadPdf]);

    if (!hasAccounting) {
        return <EvaluationAccessView />;
    }

    return (
        <>
            <GlobalStyles
                styles={{
                    '@media print': {
                        body: {
                            backgroundColor: '#fff',
                        },
                        '[data-evaluation-sidebar="true"]': {
                            display: 'none !important',
                        },
                        '[data-evaluation-actions="true"]': {
                            display: 'none !important',
                        },
                        '[data-evaluation-result="true"]': {
                            boxShadow: 'none !important',
                            breakInside: 'avoid',
                            pageBreakInside: 'avoid',
                        },
                    },
                }}
            />

            <EvaluationHero
                mode={mode}
                loading={loading || exporting}
                hasData={Boolean(data)}
                periodLabel={reportLabel}
                onDownload={handleDownload}
                onPrint={handlePrint}
                onRefresh={handleRefresh}
            />

            <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
                <Grid2 container spacing={2} alignItems="stretch">
                    <Grid2 xs={12} md={5} lg={4}>
                        <EvaluationSidebarCard
                            selectedMode={selectedMode}
                            year={year}
                            month={month}
                            quarter={quarter}
                            loading={loading}
                            onModeChange={setSelectedMode}
                            onYearChange={setYear}
                            onMonthChange={setMonth}
                            onQuarterChange={setQuarter}
                            onRun={handleRunSelected}
                        />
                    </Grid2>

                    <Grid2 xs={12} md={7} lg={8}>
                        <EvaluationResultCard
                            data={data}
                            mode={mode}
                            loading={loading}
                            error={error}
                            message={message}
                            periodLabel={reportLabel}
                            vatDetails={vatDetails}
                            vatDetailsLoading={vatDetailsLoading}
                            vatDetailsError={vatDetailsError}
                            vatDetailsOpen={vatDetailsOpen}
                            onVatDetailsToggle={handleVatDetailsToggle}
                        />
                    </Grid2>
                </Grid2>
            </Container>
        </>
    );
}
